import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { useAuth } from '../../context/AuthContext';

const AddTimesheetModal = ({ isOpen, onClose, onSubmit }) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [formData, setFormData] = useState({
    taskId: '',
    date: new Date().toISOString().split('T')[0],
    hours: '1',
    minutes: '0',
    notes: '',
    isGroupClass: false,
    isOddHour: false
  });

  // Fetch assigned tasks when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTasks();
    }
  }, [isOpen]);

  const fetchTasks = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules/tasks`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const tasksData = await response.json();
        console.log('Timesheet modal - Fetched tasks:', tasksData);
        setTasks(tasksData);
      } else {
        console.error('Failed to fetch tasks:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const selectedTask = tasks.find(t => t.id === formData.taskId);
  const totalHours = parseFloat(formData.hours) + parseFloat(formData.minutes) / 60;
  
  // Calculate payout based on group class and odd hour
  const payoutAmount = selectedTask ? (() => {
    // Step 1: Calculate base payout (group class or regular rate)
    let amount = 0;
    if (formData.isGroupClass && selectedTask.is_group_class && selectedTask.group_class_payout_rate) {
      amount = totalHours * parseFloat(selectedTask.group_class_payout_rate);
    } else {
      amount = totalHours * parseFloat(selectedTask.payout_rate || 0);
    }
    
    // Step 2: Add odd hour payout if applicable
    if (formData.isOddHour && selectedTask.is_odd_hour && selectedTask.odd_hour_payout) {
      amount += parseFloat(selectedTask.odd_hour_payout);
    }
    
    return amount;
  })() : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAccessToken();
      if (!token) {
        alert('Please log in to create timesheets');
        return;
      }

      const hours_worked = parseFloat(formData.hours) + parseFloat(formData.minutes) / 60;

      const timesheetPayload = {
        task_id: formData.taskId,
        work_date: formData.date,
        hours_worked: hours_worked,
        description: formData.notes,
        is_group_class: formData.isGroupClass,
        is_odd_hour: formData.isOddHour
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(timesheetPayload)
        }
      );

      if (response.ok) {
        const createdTimesheet = await response.json();
        onSubmit(createdTimesheet);
        setFormData({ taskId: '', date: new Date().toISOString().split('T')[0], hours: '1', minutes: '0', notes: '', isGroupClass: false, isOddHour: false });
        alert('Timesheet created successfully! Pending admin approval.');
      } else {
        const error = await response.json();
        alert(`Failed to create timesheet: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to create timesheet:', error);
      alert('Failed to create timesheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Timesheet Entry</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-3">
          {tasks.length === 0 ? (
            <div className="text-center py-8 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-amber-700 dark:text-amber-300 mb-2">No tasks assigned yet</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Please contact admin to assign tasks before creating timesheets.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Task *</label>
                <Select value={formData.taskId} onValueChange={(value) => setFormData({...formData, taskId: value, isGroupClass: false, isOddHour: false})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose assigned task" />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.map(task => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.title} - {task.subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

          {selectedTask && selectedTask.is_group_class && (
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="groupClassTimesheetCheckbox"
                  checked={formData.isGroupClass}
                  onCheckedChange={(checked) => setFormData({...formData, isGroupClass: checked})}
                />
                <label htmlFor="groupClassTimesheetCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                  This was a Group Class session
                </label>
              </div>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-2">
                {formData.isGroupClass 
                  ? `Using group class rate: ₹${selectedTask.group_class_payout_rate}/hour`
                  : `Using regular rate: ₹${selectedTask.payout_rate}/hour`
                }
              </p>
            </div>
          )}

          {selectedTask && selectedTask.is_odd_hour && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="oddHourTimesheetCheckbox"
                  checked={formData.isOddHour}
                  onCheckedChange={(checked) => setFormData({...formData, isOddHour: checked})}
                />
                <label htmlFor="oddHourTimesheetCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                  This was an Odd Hour session
                </label>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                {formData.isOddHour 
                  ? `Adding odd hour payout: +₹${selectedTask.odd_hour_payout}`
                  : `Check this box to add odd hour payout (+₹${selectedTask.odd_hour_payout})`
                }
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Date *</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              required
              max={new Date().toISOString().split('T')[0]}
              min={(() => {
                const today = new Date();
                const firstDayPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                return firstDayPrevMonth.toISOString().split('T')[0];
              })()}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              You can select dates from current and previous month only
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Hours *</label>
              <Input
                type="number"
                value={formData.hours}
                onChange={(e) => setFormData({...formData, hours: e.target.value})}
                required
                min="0"
                max="12"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Minutes *</label>
              <Select value={formData.minutes} onValueChange={(value) => setFormData({...formData, minutes: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="45">45</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Session Notes *</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Describe what was covered in this session..."
              required
              rows={3}
            />
          </div>

          {selectedTask && (
            <div className="bg-green-50 dark:bg-gray-700 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Session Summary:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Total Duration:</span>
                  <span className="ml-2 font-semibold text-gray-900 dark:text-white">{formData.hours}h {formData.minutes}m</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Your Payout:</span>
                  <span className="ml-2 font-semibold text-green-600">₹{payoutAmount.toFixed(2)}</span>
                </div>
              </div>
              {(selectedTask.is_group_class || selectedTask.is_odd_hour) && (
                <div className="pt-2 border-t border-green-200 dark:border-green-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Rate: ₹{formData.isGroupClass ? selectedTask.group_class_payout_rate : selectedTask.payout_rate}/hour 
                    {formData.isGroupClass && ' (Group Class)'}
                    {formData.isOddHour && selectedTask.odd_hour_payout && ` + ₹${selectedTask.odd_hour_payout} (Odd Hour)`}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-3 sticky bottom-0 bg-white dark:bg-gray-800 -mx-6 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={!formData.taskId || loading || tasks.length === 0}>
              {loading ? 'Creating...' : 'Create Timesheet'}
            </Button>
          </div>
          </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTimesheetModal;
